/**
 * Functionality to validate and aggregate overlaydata from TVSpotBundles
 * passed to the EntryCreationDialog. In general what makes this a bit
 * more complicated is the fact that we can activate multiple editable
 * TVSpots at once.
 *
 * Depends on:
 *   - this.contents
 *   - this.show
 *   - this.$refs.overlaydata (multiple, with a 'data-spotid' html attribute)
 */

import {getMainTVSpotFromBundle} from '@/utils';
import {getDefaultOverlayDataForOverlayOptions} from '@/mixins/overlayOptions.js';
import {onEditableTVSpotTemplateImageReplacementCropped} from '@/mixins/imageReplacement';
import {
  replaceOverlayDataImageFieldValuesWithDefaults,
  replaceOverlayDataStaticFieldValues,
} from '@/mixins/overlayDataPreview.js';

export default {
  data() {
    return {
      // Stored for reactive bindings.
      isAllOverlayDataValid: false,

      // We need to know what TVSpot we are trying to do the image
      // replacement/upload for, across a multistep dialog detached from
      // the triggering component. This is why we keep this intermediate
      // reference.
      currentTvspotRequestingImageChangeFor: null,
    };
  },
  computed: {
    bundlesWithOverlayOptions() {
      if (!this.contents) {
        return [];
      }
      const bundles = this.contents.filter(
        (content) => content.kind === 'tvspotbundle'
      );
      return bundles.filter((bundle) => {
        const mainTvSpot = getMainTVSpotFromBundle(bundle);
        // Augment with mainTvSpot attribute for cached access in v-for loop.
        bundle.mainTvSpot = mainTvSpot;

        const hasOverlayOptions =
          mainTvSpot.overlay_options &&
          Object.keys(mainTvSpot.overlay_options).length > 0;
        return hasOverlayOptions;
      });
    },
    contentHasOverlayOptions() {
      return this.bundlesWithOverlayOptions.length > 0;
    },
  },
  watch: {
    show: function (show) {
      if (show) {
        // Perform initial validation of overlaydata widgets.
        this.$nextTick(() => {
          this.validateOverlayDataWidgets();
        });
      }
    },
  },
  methods: {
    getSpotsOverlayData(branding = null) {
      const spotIdToOverlayData = {};

      const widgets = this.$refs.overlaydata || [];
      for (let widget of widgets) {
        const spotId = widget.$el.getAttribute('data-spotid');
        const overlayData = widget.getOverlayData();

        overlayData.branding = {};
        if (branding) {
          overlayData.branding = branding;
        }

        this.injectTvSpotDurationIntoOverlayData(spotId, overlayData);

        spotIdToOverlayData[spotId] = overlayData;
      }

      return spotIdToOverlayData;
    },
    /**
     * Validate all widgets: if one is invalid, disable form submission.
     */
    validateOverlayDataWidgets() {
      let allValid = true;
      const widgets = this.$refs.overlaydata;
      if (widgets) {
        widgets.forEach((ref) => {
          const isValid = ref.checkIfAllFieldsAreValid();
          if (!isValid) {
            allValid = false;
          }
        });
      }
      this.isAllOverlayDataValid = allValid;
      return this.isAllOverlayDataValid;
    },
    getMainTVSpotFromBundlesById(mainTvSpotId) {
      const bundles = this.contents.filter(
        (content) => content.kind === 'tvspotbundle'
      );
      for (let bundle of bundles) {
        const mainTvSpot = getMainTVSpotFromBundle(bundle);
        if (mainTvSpot.id == mainTvSpotId) {
          return mainTvSpot;
        }
      }
      return null;
    },
    injectTvSpotDurationIntoOverlayData(mainTvSpotId, overlayData) {
      const tvspot = this.getMainTVSpotFromBundlesById(mainTvSpotId);
      if (!tvspot) {
        return;
      }

      overlayData.duration = tvspot.duration;
    },
    populateAllIframesWithInitialOverlayData(branding = null) {
      this.refreshAllIframesWithOverlayData(branding);
    },
    refreshAllIframesWithOverlayData(branding = null) {
      const overlayDataWidgets = this.$refs.overlaydata;
      const previewWidgets = this.$refs.preview;
      if (!previewWidgets) {
        return;
      }

      for (let i = 0; i < overlayDataWidgets.length; i++) {
        const overlayDataWidget = overlayDataWidgets[i];

        const overlayDataMainTvSpotId =
          overlayDataWidget.$el.getAttribute('data-spotid');

        // This contains the actual user input.
        let overlayData = overlayDataWidget.getOverlayData();

        // Add a Branding if we have one.
        overlayData.branding = {};
        if (branding) {
          overlayData.branding = branding;
        }

        // Make sure we have a duration.
        this.injectTvSpotDurationIntoOverlayData(
          overlayDataMainTvSpotId,
          overlayData
        );

        // Identify the preview widgets that belong to this bundle.
        for (const previewWidget of previewWidgets) {
          const previewWidgetMainTvSpotId =
            previewWidget.$el.getAttribute('data-maintvspotid');
          const previewWidgetTvSpotId =
            previewWidget.$el.getAttribute('data-tvspotid');

          if (previewWidgetMainTvSpotId === overlayDataMainTvSpotId) {
            // HACK: IMAGE fields in a bundle can be format-specific
            //   and may need a different default value. For now, we'll
            //   assume that if we have a bundle with multiple TVSpots and
            //   we have IMAGE fields, that those are are not meant to be
            //   edited by the User but instead we want to use the value
            //   used during the production of each TVSpot.
            //
            //   This will only fix the preview in this dialog, not the
            //   actual data used on the backend.
            //
            //   This will not work when the IMAGE fields are meant to
            //   be user-editable, but we'd need actual format-specific
            //   UI for that anyway, which is currently not the case.
            //
            //   See https://github.com/myshopradio/cms/issues/2505
            //
            //   Get default overlayData for the specific TVSpot to get
            //   the format-specific IMAGE defaults. Replace value for
            //   IMAGE fields with those format-specific defaults to
            //   avoid e.g. using a landscape image for a portrait
            //   TVSpot.
            if (previewWidgets.length > 1) {
              // Find correct TVSpot object and update overlayData.
              for (const content of this.contents) {
                if (content.main_tvspot_id == previewWidgetMainTvSpotId) {
                  for (const tvspot of content.tvspots) {
                    if (tvspot.id == previewWidgetTvSpotId) {
                      const tvspotDefaultOverlayData =
                        getDefaultOverlayDataForOverlayOptions(
                          tvspot.overlay_options
                        );

                      // IMAGE fields
                      overlayData =
                        replaceOverlayDataImageFieldValuesWithDefaults(
                          overlayData,
                          tvspotDefaultOverlayData,
                          tvspot.overlay_options
                        );

                      // static_fields
                      overlayData = replaceOverlayDataStaticFieldValues(
                        overlayData,
                        tvspot.overlay_options.static_fields || {}
                      );
                      break;
                    }
                  }
                }
              }
            }

            previewWidget.updateOverlayData(overlayData);
          }
        }
      }
    },
    onEntryCreationDialogOverlayDataChanged(branding = null) {
      this.validateOverlayDataWidgets();
      this.refreshAllIframesWithOverlayData(branding);
    },
    onEntryCreationDialogImageChangeRequested(tvspot, opts) {
      this.currentTvspotRequestingImageChangeFor = tvspot;
      this.$refs.imagereplacement.onImageChangeRequested(opts);
    },
    onEntryCreationDialogImageChangeConfirmed(payload) {
      const tvspot = this.currentTvspotRequestingImageChangeFor;

      onEditableTVSpotTemplateImageReplacementCropped(
        tvspot.editable_template.id,
        tvspot.overlay_options,
        payload.fieldName,
        payload.file,
        payload.originalFilename
      );
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/entryCreationDialogOverlayData.js